import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import { categories, allProductsByCategory, articlesList } from "@/mocks/categoryData";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || "fragrance");

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const isArticles = activeSlug === "articles";
  const products = allProductsByCategory[activeSlug] || [];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-24">
        {/* Category Header */}
        <section className="w-full px-6 md:px-10 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: activeCategory.name }]} />
            <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-900 mb-2">
                  {activeCategory.name}
                </h1>
                <p className="text-sm text-foreground-500">{activeCategory.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full px-6 md:px-10 sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
          <div className="max-w-7xl mx-auto py-3">
            <div className="flex items-center gap-1 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-500 hover:bg-background-100 hover:text-foreground-800"
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="w-full px-6 md:px-10 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            {isArticles ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {articlesList.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        title={article.title}
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs px-2 py-1 bg-accent-100 text-accent-700 rounded-full font-medium">
                          {article.category}
                        </span>
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                      </div>
                      <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-500 line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-product-shop>
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="relative aspect-[4/5] overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        title={product.name}
                      />
                      {product.tag && (
                        <span className="absolute top-3 left-3 px-3 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full">
                          {product.tag}
                        </span>
                      )}
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                          {product.name}
                        </h3>
                        <span className="text-xs text-foreground-400 whitespace-nowrap mt-1">{product.volume}</span>
                      </div>
                      <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 mb-3">{product.description}</p>
                      <p className="font-heading text-lg font-semibold text-primary-600">{product.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isArticles && products.length === 0 && (
              <div className="text-center py-16">
                <i className="ri-inbox-line text-4xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">暂无产品，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "首页", item: "https://meirouzhixiang.com" },
                  { "@type": "ListItem", position: 2, name: activeCategory.name },
                ],
              },
              {
                "@type": "WebPage",
                url: `https://meirouzhixiang.com/category/${activeSlug}`,
                name: `${activeCategory.name} - 媚肉之香`,
                description: activeCategory.description,
              },
            ],
          }),
        }}
      />
    </>
  );
}